import { useEffect, useMemo, useState } from 'react';
import { IsRestoringProvider, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClientRestore, persistQueryClientSubscribe } from '@tanstack/react-query-persist-client';
import { hoursToMilliseconds } from 'date-fns';
import { useStore } from 'common/store';
import type { DefaultMutationResponse } from 'common/types/services';
import type { UseStoreValues } from 'common/types/store';
import { getStoredAccessTokenUserId } from 'pages/Auth/utils';
import { buildReactQueryCacheBuster } from 'services/storage/reactQueryCacheScope';
import { createReactQueryIndexedDBPersister } from 'services/storage/reactQueryIndexedDBPersister';
import { displayErrorMessage, displaySuccessMessage, getObjectValue } from 'utilities';
import type { PersistedQueryProviderProps } from './PersistedQueryProvider.props';

const persister = createReactQueryIndexedDBPersister();

const createQueryClient = (): QueryClient =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error: unknown, query) => {
        if (query.meta?.showGlobalError === false) {
          return;
        }

        displayErrorMessage(error instanceof Error ? error.message : (error as string));
      },
    }),
    defaultOptions: {
      queries: {
        gcTime: hoursToMilliseconds(24),
        retry: false,
        retryOnMount: false,
      },
      mutations: {
        onError: (error: unknown) => displayErrorMessage(error instanceof Error ? error.message : (error as string)),
        onSuccess: data => {
          const { responseMessage } = getObjectValue(data) as DefaultMutationResponse;

          if (responseMessage) {
            displaySuccessMessage(responseMessage);
          }
        },
      },
    },
  });

const PersistedQueryProvider = ({ children }: PersistedQueryProviderProps) => {
  const [{ user }] = useStore() as UseStoreValues;
  // The token scope is available before profile hydration, avoiding an anonymous
  // cache restore during app startup. Store state takes over after authentication.
  const storedUserId = useMemo(() => (user?.id == null ? getStoredAccessTokenUserId() : null), [user?.id]);
  const cacheBuster = buildReactQueryCacheBuster({
    apiEndpoint: process.env.REACT_APP_API_ENDPOINT,
    userId: user?.id ?? storedUserId,
  });
  const queryClient = useMemo(createQueryClient, [cacheBuster]);
  const [restoredQueryClient, setRestoredQueryClient] = useState<QueryClient | null>(null);

  // Restore and subscribe per scope without keying this provider and remounting the app shell.
  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    const persistOptions = {
      buster: cacheBuster,
      persister,
      queryClient,
      dehydrateOptions: {
        // Alexandria metric-series responses can be several MB and are short-lived.
        // Queries marked with `meta: { persist: false }` remain in React Query's
        // in-memory cache but are excluded from the app-wide IndexedDB cache.
        shouldDehydrateQuery: query => query.state.status === 'success' && query.meta?.persist !== false,
      },
    };

    persistQueryClientRestore(persistOptions)
      .catch(() => undefined)
      .finally(() => {
        if (cancelled) {
          return;
        }

        unsubscribe = persistQueryClientSubscribe(persistOptions);
        setRestoredQueryClient(queryClient);
      });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [cacheBuster, queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <IsRestoringProvider value={restoredQueryClient !== queryClient}>{children}</IsRestoringProvider>
    </QueryClientProvider>
  );
};

export default PersistedQueryProvider;
